import React, { useState } from "react";
import { cn } from "../../../common/u1/cn";
import { Button } from "../../../common/u1/Button";
import { Pagination, PaginationSummary } from "../../../common/u1/Pagination";
import { InvoiceTable } from "./InvoiceTable";
import { Dirham, DownloadIcon } from "../../../../assets/ui/icons";
import { formatAed, totalPaid } from "./billingFormat";

/* Billing history — the payments table, its pager and its footer total. */
export const PAGE_SIZE = 5;

export const BillingHistoryCard = ({
  rows = [],
  onDownload,
  onDownloadAll,
  pageSize = PAGE_SIZE,
  /* Server paging, when it exists: supply these and `rows` is taken as the
     page rather than the whole set. */
  page: pageProp,
  totalPages: totalPagesProp,
  total: totalProp,
  /* Server paging only: with one page in hand the rows cannot be summed, so
     the figure has to come with them. */
  totalAmount,
  onPageChange,
  className = "",
}) => {
  const [localPage, setLocalPage] = useState(1);
  const server = typeof onPageChange === "function";

  const total = typeof totalProp === "number" ? totalProp : rows.length;
  const totalPages = server
    ? Math.max(1, totalPagesProp || 1)
    : Math.max(1, Math.ceil(total / pageSize));

  /* Clamped during render, as FollowingList does: a shrinking list must not
     leave the user on a page past the end and paint an empty table first. */
  const current = server ? pageProp || 1 : Math.min(localPage, totalPages);
  const visible = server
    ? rows
    : rows.slice((current - 1) * pageSize, current * pageSize);

  const goTo = server ? onPageChange : setLocalPage;
  const sum = typeof totalAmount === "number" ? totalAmount : totalPaid(rows);

  return (
    <section
      className={cn(
        "overflow-hidden rounded-[18px] border border-[var(--line)] bg-white",
        "shadow-[0_1px_2px_rgba(20,40,60,.03)]",
        className
      )}
      aria-labelledby="u1-history-heading"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 p-5 sm:px-[26px] sm:pt-6">
        <div className="min-w-0">
          <h3
            id="u1-history-heading"
            className="m-0 text-[19px] font-bold text-[var(--ink)] font-sans"
          >
            Billing history
          </h3>
          <p className="mt-1 mb-0 text-[13.5px] text-[var(--muted)] font-sans">
            Every payment you have made on TREVEX appears here.
          </p>
        </div>

        {/* Kept enabled only when there is something to download — the live
            control fires a loop over an empty list otherwise. */}
        <Button
          variant="coral"
          size="sm"
          onClick={onDownloadAll}
          disabled={rows.length === 0}
          className="shrink-0"
        >
          <DownloadIcon size={15} />
          Download all
        </Button>
      </div>

      <InvoiceTable rows={visible} onDownload={onDownload} />

      {/* The pager sits between the table and the footer bar. It draws nothing
          when there is only one page. */}
      {totalPages > 1 && (
        <div className="px-5 pb-1 sm:px-[26px]">
          <Pagination
            page={current}
            totalPages={totalPages}
            onChange={goTo}
            label="Billing history pages"
          />
        </div>
      )}

      <div
        className={cn(
          "flex flex-wrap items-center justify-between gap-2 border-t border-[var(--line)]",
          "bg-[var(--bg)] px-5 py-3.5 sm:px-[26px]"
        )}
      >
        <PaginationSummary
          page={current}
          pageSize={pageSize}
          total={total}
          itemLabel="payment"
        />
        <p className="m-0 text-[13px] font-semibold text-[var(--ink-2)] font-sans">
          Total paid:{" "}
          <span className="inline-flex items-baseline text-[var(--ink)]">
            <Dirham />
            {formatAed(sum)}
          </span>
        </p>
      </div>
    </section>
  );
};

export default BillingHistoryCard;
